/*
 * article-gallery
 *
 * Lightbox paging for the article gallery block.
 *
 * The overlay is a native <dialog> opened with showModal(), so the browser already
 * provides the focus trap, the Escape handler, focus restoration to the trigger and
 * an inert background. This class only adds what <dialog> does not: paging between
 * images, the counter, backdrop-click close, arrow keys and touch swipe.
 */
if (!customElements.get('article-gallery')) {
  customElements.define(
    'article-gallery',
    class ArticleGallery extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-gallery-lightbox]');
        if (!this.dialog) return;

        this.triggers = Array.from(this.querySelectorAll('[data-gallery-trigger]'));
        this.fullImage = this.querySelector('[data-gallery-full]');
        this.counter = this.querySelector('[data-gallery-counter]');
        this.index = 0;
        this.touchStartX = null;

        if (this.triggers.length < 2) this.dialog.setAttribute('data-single', '');

        this.triggers.forEach((trigger, i) => {
          trigger.addEventListener('click', () => this.open(i));
        });

        this.querySelector('[data-gallery-close]').addEventListener('click', () => this.dialog.close());
        this.querySelector('[data-gallery-prev]').addEventListener('click', () => this.step(-1));
        this.querySelector('[data-gallery-next]').addEventListener('click', () => this.step(1));

        this.dialog.addEventListener('click', this.onDialogClick.bind(this));
        this.dialog.addEventListener('keydown', this.onKeydown.bind(this));
        this.dialog.addEventListener('close', this.onClose.bind(this));
        this.dialog.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.dialog.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      open(index) {
        this.index = index;
        this.render();
        /*
         * showModal() is not universally available on very old Safari. Falling back to
         * the file itself is better than a button that silently does nothing.
         */
        if (typeof this.dialog.showModal !== 'function') {
          window.open(this.triggers[index].dataset.full, '_blank', 'noopener');
          return;
        }
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }

      onClose() {
        document.documentElement.style.overflow = '';
      }

      step(direction) {
        const count = this.triggers.length;
        if (count < 2) return;
        this.index = (this.index + direction + count) % count;
        this.render();
      }

      render() {
        const trigger = this.triggers[this.index];
        if (!trigger) return;
        this.fullImage.src = trigger.dataset.full;
        this.fullImage.alt = trigger.dataset.alt || '';
        if (this.counter) {
          this.counter.textContent = `${this.index + 1} / ${this.triggers.length}`;
        }
      }

      /*
       * Close on any click that is not the image or a control. Testing for the dialog
       * itself is not enough: the stage fills the dialog, so clicks on the dark area
       * around the image land on the stage and would otherwise be ignored.
       */
      onDialogClick(evt) {
        if (evt.target.closest('[data-gallery-full], button')) return;
        this.dialog.close();
      }

      onKeydown(evt) {
        if (evt.key === 'ArrowLeft') {
          evt.preventDefault();
          this.step(-1);
        } else if (evt.key === 'ArrowRight') {
          evt.preventDefault();
          this.step(1);
        }
      }

      onTouchStart(evt) {
        this.touchStartX = evt.changedTouches[0].clientX;
      }

      onTouchEnd(evt) {
        if (this.touchStartX === null) return;
        const delta = evt.changedTouches[0].clientX - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(delta) < 50) return;
        this.step(delta < 0 ? 1 : -1);
      }
    }
  );
}